import React, { useState, useEffect } from 'react';
import api from '../../../config/axios';
import Swal from 'sweetalert2';
import { FaFilter, FaPlus, FaEye, FaBan, FaFilePdf } from 'react-icons/fa';
import Paginacao from '../../../components/utils/paginacao';
import DatePicker from '../../../components/utils/datepicker';
import moment from 'moment';
import ImportarArquivo from './modals/ImportarArquivo';
import DetalheImportacao from './modals/DetalheImportacao';
import GerarRelatorio from './modals/GerarRelatorio';

const ConciliacaoBancaria = () => {

    const [importacoes, setImportacoes] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRegistros, setTotalRegistros] = useState(0);

    const [filtros, setFiltros] = useState({
        contas_bancarias: [],
        status: '',
        data_inicio: null,
        data_fim: null
    });

    const [contasBancarias, setContasBancarias] = useState([]);
    const [showModalImportar, setShowModalImportar] = useState(false);
    const [showModalDetalhe, setShowModalDetalhe] = useState(false);
    const [showModalRelatorio, setShowModalRelatorio] = useState(false);
    const [importacaoSelecionada, setImportacaoSelecionada] = useState(null);

    const limit = 10;

    useEffect(() => {
        fetchContasBancarias();
    }, []);

    useEffect(() => {
        fetchImportacoes(currentPage);
    }, [currentPage]);

    const fetchContasBancarias = async () => {
        try {
            const response = await api.get('/contas-bancarias/para-lancamentos');
            setContasBancarias(response.data || []);
        } catch (error) {
            setContasBancarias([]);
        }
    };

    const fetchImportacoes = async (page, filtrosParams = filtros) => {
        try {
            Swal.fire({
                title: 'Carregando...',
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const params = {
                page: page,
                limit: limit
            };

            if (filtrosParams.contas_bancarias && filtrosParams.contas_bancarias.length > 0) {
                params.contas_bancarias = filtrosParams.contas_bancarias.join(',');
            }
            if (filtrosParams.status) {
                params.status = filtrosParams.status;
            }
            if (filtrosParams.data_inicio) {
                params.data_inicio = moment(filtrosParams.data_inicio).format('YYYY-MM-DD');
            }
            if (filtrosParams.data_fim) {
                params.data_fim = moment(filtrosParams.data_fim).format('YYYY-MM-DD');
            }

            const response = await api.get('/conciliacao', { params });

            setImportacoes(response.data.records || []);
            setTotalPages(response.data.totalPages || 1);
            setTotalRegistros(response.data.totalRegistros || 0);

            Swal.close();
        } catch (error) {
            Swal.fire('Erro', 'Erro ao carregar importações', 'error');
        }
    };

    const handleFiltrar = () => {
        setCurrentPage(1);
        fetchImportacoes(1, filtros);
    };

    const limparFiltros = () => {
        const filtrosLimpos = {
            contas_bancarias: [],
            status: '',
            data_inicio: null,
            data_fim: null
        };
        setFiltros(filtrosLimpos);
        setCurrentPage(1);
        fetchImportacoes(1, filtrosLimpos);
    };

    const handleContaBancariaChange = (contaId) => {
        setFiltros(prev => {
            const contasAtual = [...prev.contas_bancarias];
            if (contasAtual.includes(contaId)) {
                return { ...prev, contas_bancarias: contasAtual.filter(id => id !== contaId) };
            } else {
                return { ...prev, contas_bancarias: [...contasAtual, contaId] };
            }
        });
    };

    const removerContaBancaria = (contaId) => {
        setFiltros(prev => ({
            ...prev,
            contas_bancarias: prev.contas_bancarias.filter(id => id !== contaId)
        }));
    };

    const formatarData = (dataString) => {
        if (!dataString) return '-';

        if (dataString.includes('-')) {
            const [ano, mes, dia] = dataString.split('T')[0].split('-');
            return `${dia}/${mes}/${ano}`;
        }

        const data = new Date(dataString);
        return data.toLocaleDateString('pt-BR');
    };

    const formatarDataHora = (dataString) => {
        if (!dataString) return '-';

        const data = new Date(dataString);
        const dataFormatada = data.toLocaleDateString('pt-BR');
        const horaFormatada = data.toLocaleTimeString('pt-BR');

        return `${dataFormatada} ${horaFormatada}`;
    };

    const formatarValor = (valor) => {
        if (!valor) return 'R$ 0,00';
        return parseFloat(valor).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        });
    };

    const formatarStatus = (status) => {
        const statusMap = {
            'pendente': 'Pendente',
            'em_processamento': 'Processando',
            'conciliado_parcial': 'Parcial',
            'conciliado_total': 'Conciliado',
            'parcial': 'Parcial',
            'conciliado': 'Conciliado',
            'erro': 'Erro',
            'cancelado': 'Cancelado'
        };
        return statusMap[status] || (status ? status.charAt(0).toUpperCase() + status.slice(1) : status);
    };

    const getCorStatus = (status) => {
        const cores = {
            'pendente': 'bg-warning',
            'em_processamento': 'bg-info',
            'conciliado_parcial': 'bg-primary',
            'conciliado_total': 'bg-success',
            'parcial': 'bg-primary',
            'conciliado': 'bg-success',
            'erro': 'bg-danger',
            'cancelado': 'bg-secondary'
        };
        return cores[status] || 'bg-secondary';
    };


    const handleNovaImportacao = () => {
        setShowModalImportar(true);
    };

    const handleVerDetalhe = (importacao) => {
        setImportacaoSelecionada(importacao);
        setShowModalDetalhe(true);
    };

    const handleImportacaoSalva = (importacaoCriada = null) => {
        fetchImportacoes(currentPage);
        if (importacaoCriada) {
            setImportacaoSelecionada(importacaoCriada);
            setShowModalDetalhe(true);
        }
    };

    const handleCancelar = async (importacao) => {
        const temConciliadas = importacao.transacoes_conciliadas > 0;

        const result = await Swal.fire({
            title: 'Tem certeza?',
            html: temConciliadas
                ? `<p>Esta importação possui <strong>${importacao.transacoes_conciliadas}</strong> transações conciliadas.</p><p>Ao cancelar, as conciliações serão desfeitas e os lançamentos criados serão cancelados.</p><p><strong>${importacao.nome_arquivo}</strong></p>`
                : `<p>Deseja realmente cancelar esta importação?</p><p><strong>${importacao.nome_arquivo}</strong></p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim, cancelar!',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                Swal.fire({
                    title: 'Cancelando...',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                await api.post(`/conciliacao/${importacao.id}/cancelar`);

                Swal.fire('Sucesso', 'Importação cancelada com sucesso', 'success');
                fetchImportacoes(currentPage);
            } catch (error) {
                Swal.fire('Erro', error.response?.data?.message || 'Erro ao cancelar importação', 'error');
            }
        }
    };

    return (
        <div className="container-fluid">
            <div className="card">
                <div className="card-body">
                    <div className="row mt-3">
                        <div className="col-md-6">
                            <h4 className="fw-bold mb-0">Conciliação Bancária</h4>
                        </div>
                    </div>

                    <div className="row mt-3 mb-3">
                        <div className="col-md-6 col-12 d-flex gap-2">
                            <button className="btn btn-outline-primary" onClick={handleNovaImportacao}>
                                <FaPlus className="me-1" /> Importar Arquivo
                            </button>
                            <button className="btn btn-outline-secondary" onClick={() => setShowModalRelatorio(true)}>
                                <FaFilePdf className="me-1" /> Gerar Relatório
                            </button>
                        </div>
                    </div>

                    <div className="row mb-4 align-items-end mt-4">
                        <div className="col-lg-2 col-12">
                            <label className="form-label fw-bold">Contas Bancárias</label>
                            <div className="dropdown position-relative">
                                <div
                                    className="form-control d-flex flex-wrap align-items-center gap-1"
                                    style={{ minHeight: '38px', cursor: 'pointer', paddingRight: '30px' }}
                                    data-bs-toggle="dropdown"
                                >
                                    {filtros.contas_bancarias.length === 0 ? (
                                        <span className="text-muted">Todas as contas selecionadas</span>
                                    ) : (
                                        filtros.contas_bancarias.map(contaId => {
                                            const conta = contasBancarias.find(c => c.id === contaId);
                                            return conta ? (
                                                <span key={contaId} className="badge d-flex align-items-baseline" style={{ backgroundColor: '#172228', color: '#fff' }}>
                                                    <span style={{ fontSize: '0.8em' }}>
                                                        {conta.nome_exibicao || conta.nome}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="btn-close btn-close-white ms-1"
                                                        style={{ fontSize: '0.6em' }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            removerContaBancaria(contaId);
                                                        }}
                                                    ></button>
                                                </span>
                                            ) : null;
                                        })
                                    )}
                                </div>
                                <i
                                    className="fas fa-chevron-down position-absolute text-muted"
                                    style={{ top: '50%', right: '10px', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                                ></i>
                                <ul className="dropdown-menu w-100" onClick={(e) => e.stopPropagation()}>
                                    <li>
                                        <button
                                            className="dropdown-item"
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setFiltros(prev => ({ ...prev, contas_bancarias: [] }));
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                className="form-check-input me-2"
                                                checked={filtros.contas_bancarias.length === 0}
                                                readOnly
                                            />
                                            Todas as contas
                                        </button>
                                    </li>
                                    <li><hr className="dropdown-divider" /></li>
                                    {contasBancarias.map(conta => (
                                        <li key={conta.id}>
                                            <button
                                                className="dropdown-item"
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleContaBancariaChange(conta.id);
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input me-2"
                                                    checked={filtros.contas_bancarias.includes(conta.id)}
                                                    readOnly
                                                />
                                                {conta.nome_exibicao || conta.nome}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <div className="col-lg-2 col-12 mt-lg-0 mt-3">
                            <label className="form-label fw-bold">Status</label>
                            <select
                                className="form-select"
                                value={filtros.status}
                                onChange={(e) => setFiltros(prev => ({ ...prev, status: e.target.value }))}
                            >
                                <option value="">Todos</option>
                                <option value="pendente">Pendente</option>
                                <option value="conciliado">Conciliado</option>
                                <option value="cancelado">Cancelado</option>
                            </select>
                        </div>
                        <div className="col-lg-2 col-12 mt-lg-0 mt-3">
                            <label className="form-label fw-bold">Período De</label>
                            <DatePicker
                                selected={filtros.data_inicio}
                                onChange={(date) => setFiltros(prev => ({ ...prev, data_inicio: date }))}
                            />
                        </div>
                        <div className="col-lg-2 col-12 mt-lg-0 mt-3">
                            <label className="form-label fw-bold">Período Até</label>
                            <DatePicker
                                selected={filtros.data_fim}
                                onChange={(date) => setFiltros(prev => ({ ...prev, data_fim: date }))}
                            />
                        </div>
                        <div className="col-lg-4 col-12 mt-lg-0 mt-3 text-end d-flex align-items-end justify-content-end">
                            <button className="btn btn-outline-secondary me-2" onClick={limparFiltros}>
                                Limpar
                            </button>
                            <button className="btn btn-custom-primary" onClick={handleFiltrar}>
                                <FaFilter className="me-1" /> Filtrar
                            </button>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table className="table table-striped table-hover">
                            <thead className="table-dark">
                                <tr>
                                    <th className="text-nowrap">Arquivo</th>
                                    <th className="text-nowrap">Período</th>
                                    <th className="text-nowrap">Conta</th>
                                    <th className="text-nowrap text-center">Transações</th>
                                    <th className="text-nowrap">Data da Importação</th>
                                    <th className="text-nowrap">Status</th>
                                    <th className="text-nowrap text-center">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {importacoes.length > 0 ? (
                                    importacoes.map((importacao) => (
                                        <tr key={importacao.id}>
                                            <td style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={importacao.nome_arquivo}>
                                                {importacao.nome_arquivo}
                                            </td>
                                            <td className="text-nowrap">
                                                {formatarData(importacao.periodo_inicio)} - {formatarData(importacao.periodo_fim)}
                                            </td>
                                            <td className="text-nowrap">
                                                {importacao.contaBancaria?.nome_exibicao || importacao.contaBancaria?.nome || '-'}
                                            </td>
                                            <td className="text-nowrap text-center">
                                                {importacao.transacoes_conciliadas || 0} / {importacao.total_transacoes || 0}
                                            </td>
                                            <td className="text-nowrap">
                                                {formatarDataHora(importacao.created_at)}
                                            </td>
                                            <td className="text-nowrap">
                                                <span className={`badge ${getCorStatus(importacao.status)}`}>
                                                    {formatarStatus(importacao.status)}
                                                </span>
                                            </td>
                                            <td className="text-nowrap text-center">
                                                <button
                                                    className="btn btn-custom-secondary btn-sm"
                                                    onClick={() => handleVerDetalhe(importacao)}
                                                    title="Ver detalhes"
                                                >
                                                    <FaEye />
                                                </button>
                                                {!['cancelado', 'conciliado', 'conciliado_total'].includes(importacao.status) && (
                                                    <button
                                                        className="btn btn-custom-secondary btn-sm ms-3"
                                                        onClick={() => handleCancelar(importacao)}
                                                        title="Cancelar"
                                                    >
                                                        <FaBan />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center">
                                            Nenhuma importação encontrada
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <div>
                            <span>
                                Total de registros: <strong>{totalRegistros}</strong>
                            </span>
                        </div>
                        <Paginacao
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                </div>
            </div>

            {showModalImportar && (
                <ImportarArquivo
                    onClose={() => setShowModalImportar(false)}
                    onImportacaoSalva={handleImportacaoSalva}
                    contasBancarias={contasBancarias}
                />
            )}

            {showModalDetalhe && importacaoSelecionada && (
                <DetalheImportacao
                    importacao={importacaoSelecionada}
                    onClose={() => {
                        setShowModalDetalhe(false);
                        setImportacaoSelecionada(null);
                    }}
                    onAtualizar={handleImportacaoSalva}
                />
            )}

            {showModalRelatorio && (
                <GerarRelatorio
                    contasBancarias={contasBancarias}
                    onClose={() => setShowModalRelatorio(false)}
                />
            )}
        </div>
    );
};

export default ConciliacaoBancaria;
